import { useEffect } from "react";
import { toast } from "react-toastify";

import AccountService from "src/Services/API/account.service";
import CustomerService from "src/Services/API/customer.service";
import InviteService from "src/Services/API/invite.service";
import UserService from "src/Services/API/user.service";
import VendorService from "src/Services/API/vendor.service";
import { handleError } from "src/Common/function";
import Loader from "src/Components/Common/Loader";

// The CSV templates offered for download. Kept as plain strings so the download
// works offline and the examples always match what the parser expects.
const CUSTOMER_TEMPLATE = `Customer Name,Location Name,Address,Contact Name,Phone,Email,Branch
Acme Retail Group,Acme Retail Group - HQ,"120 Market St, Springfield, IL 62701",Sarah Chen,217-555-0142,sarah.chen@acmeretail.com,Springfield Branch
Acme Retail Group,Acme Retail Group - Westside,"48 Westside Blvd, Springfield, IL 62704",Miguel Torres,217-555-0198,miguel.torres@acmeretail.com,Springfield Branch
Coastal Properties LLC,Coastal Properties - Marina Complex,"77 Harbor View Rd, Bldg A, Newport, RI 02840",Elena Ruiz,401-555-0456,elena.ruiz@coastalprop.com,
Downtown Dental Clinic,Downtown Dental Clinic,"305 Main St, Suite 200, Springfield, IL 62701",Dr. Amanda Lee,217-555-0233,frontdesk@downtowndental.com,
`;

const INVITE_TEMPLATE = `Email,First Name,Last Name,Branch
sarah.chen@acmehvac.com,Sarah,Chen,Springfield Branch
miguel.torres@acmehvac.com,Miguel,Torres,Springfield Branch
priya.nair@acmehvac.com,Priya,Nair,
tom.riggins@acmehvac.com,Tom,Riggins,
elena.ruiz@acmehvac.com,Elena,Ruiz,
`;

const VENDOR_TEMPLATE = `Vendor Name
Coastal HVAC Supply Co.
Apex Refrigeration Parts
Statewide Electrical Wholesale
`;

// Minimal RFC-4180 parser. The templates carry quoted, comma-holding addresses,
// so splitting on commas would shear rows apart — quotes have to be honoured.
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          value += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        value += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(value);
      value = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(value);
      value = "";
      rows.push(row);
      row = [];
    } else {
      value += char;
    }
  }
  if (value !== "" || row.length) {
    row.push(value);
    rows.push(row);
  }
  // Spreadsheet exports often end with blank lines; they aren't data.
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ""));
};

// Column positions by header name, so a file with reordered or extra columns
// still imports as long as the named columns are there.
const headerIndex = (headerRow) => {
  const map = {};
  headerRow.forEach((cell, i) => {
    map[cell.trim().toLowerCase()] = i;
  });
  return map;
};

const cellAt = (cells, index) => (index === undefined ? "" : (cells[index] || "").trim());

const missingColumns = (index, required) => required.filter((name) => index[name.toLowerCase()] === undefined);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const downloadTemplate = (fileName, content) => {
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

// One section's upload/import state. Held by the Onboarding page rather than
// here, so leaving the step and coming back keeps the previews and per-row
// results on screen.
export const EMPTY_IMPORT = { fileName: "", rows: [], error: "", importing: false, dragging: false };

// error: the row can never import as-is (bad file data). failed: the server
// refused this attempt — the row stays importable so a retry can pick it up.
const STATUS_CHIPS = {
  ready: { label: "Ready", style: "text-subTitleColor bg-gray-100" },
  error: { label: "Error", style: "text-red-600 bg-red-50" },
  failed: { label: "Failed", style: "text-red-600 bg-red-50" },
  imported: { label: "Imported", style: "text-green-700 bg-green-50" },
  skipped: { label: "Skipped", style: "text-amber-700 bg-amber-50" },
};

const importable = (row) => row.status === "ready" || row.status === "failed";

// Step 6 — bring the data the company already has: customers with their
// locations, the team roster, and vendors. Everything here writes real records
// through the same endpoints the Settings pages use, and every section is
// optional — a company can skip the whole step and add data later.
function YourData({
  branches,
  roles,
  onImported,
  onBusyChange,
  customers,
  setCustomers,
  invites,
  setInvites,
  vendors,
  setVendors,
  inviteRoleId,
  setInviteRoleId,
}) {
  const currentUser = UserService.getCurrentUser();

  // The role every invitation in the file is sent with. "User" (seeded with
  // every company) is the default, but it may have been renamed or removed on
  // the Team step, so the pick stays open.
  const inviteRoles = roles.filter((role) => role.name !== "Super Admin");
  useEffect(() => {
    if (inviteRoleId || !inviteRoles.length) return;
    const fallback = inviteRoles.find((role) => role.name.trim().toLowerCase() === "user") || inviteRoles[0];
    setInviteRoleId(fallback.id);
  }, [roles]);

  // A drag abandoned by navigating away leaves the flag up; re-entering the
  // step must not show a phantom drop highlight.
  useEffect(() => {
    [setCustomers, setInvites, setVendors].forEach((set) =>
      set((prev) => (prev.dragging ? { ...prev, dragging: false } : prev)),
    );
  }, []);

  // The Continue button must not race a half-finished import.
  useEffect(() => {
    onBusyChange(customers.importing || invites.importing || vendors.importing);
  }, [customers.importing, invites.importing, vendors.importing]);

  const markRow = (setState, rowIndex, patch) => {
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row, i) => (i === rowIndex ? { ...row, ...patch } : row)),
    }));
  };

  const processFile = async (file, setState, parse) => {
    // Dropped files bypass the input's accept filter, so the type is checked
    // here — before reading — for both paths.
    if (!/\.csv$/i.test(file.name) && !(file.type || "").includes("csv")) {
      setState({ ...EMPTY_IMPORT, fileName: file.name, error: "That doesn't look like a CSV file" });
      return;
    }
    try {
      const table = parseCsv(await file.text());
      if (table.length < 2) {
        setState({ ...EMPTY_IMPORT, fileName: file.name, error: "The file has no data rows" });
        return;
      }
      const result = parse(headerIndex(table[0]), table.slice(1));
      setState({ ...EMPTY_IMPORT, fileName: file.name, ...result });
    } catch (error) {
      setState({ ...EMPTY_IMPORT, fileName: file.name, error: "Couldn't read that file — is it a CSV?" });
    }
  };

  const readFile = (event, setState, parse) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // so re-picking the same (fixed) file re-parses it
    if (file) processFile(file, setState, parse);
  };

  // ---- customers & locations ----

  const parseCustomers = (index, dataRows) => {
    const missing = missingColumns(index, ["Customer Name", "Location Name"]);
    if (missing.length) {
      return {
        error: `Missing required column(s): ${missing.join(", ")} — download the template for the expected format.`,
      };
    }
    const rows = dataRows.map((cells) => {
      const branchName = cellAt(cells, index["branch"]);
      // Blank or unrecognised branch names import without a branch — the
      // location stays editable in the app, so nothing is lost.
      const branch = branchName
        ? branches.find((item) => (item.name || "").trim().toLowerCase() === branchName.toLowerCase())
        : null;
      const row = {
        customer: cellAt(cells, index["customer name"]),
        location: cellAt(cells, index["location name"]),
        address: cellAt(cells, index["address"]),
        contact: cellAt(cells, index["contact name"]),
        phone: cellAt(cells, index["phone"]),
        email: cellAt(cells, index["email"]),
        branchName,
        branchId: branch?.id || null,
        status: "ready",
        message: "",
      };
      if (!row.customer) return { ...row, status: "error", message: "Customer Name is required" };
      if (!row.location) return { ...row, status: "error", message: "Location Name is required" };
      if (row.email && !isValidEmail(row.email)) return { ...row, status: "error", message: "Invalid email" };
      if (branchName && !branch)
        return { ...row, message: `Branch "${branchName}" not found — imports without a branch` };
      return row;
    });
    return { rows };
  };

  const importCustomers = async () => {
    setCustomers((prev) => ({ ...prev, importing: true }));
    try {
      // Rows sharing a Customer Name become locations of one customer — and a
      // customer that already exists (an earlier partial import, or one created
      // by hand) gains locations instead of a duplicate. Locations the customer
      // already has are skipped for the same reason, so re-running a fixed file
      // never doubles anything up.
      const response = await AccountService.getAll();
      const accounts = Array.isArray(response.data) ? response.data : [];
      const existing = new Map(accounts.map((account) => [(account.name || "").trim().toLowerCase(), account]));
      const existingLocations = new Map(
        accounts.map((account) => [
          (account.name || "").trim().toLowerCase(),
          new Set((account.customers || []).map((location) => (location.companyName || "").trim().toLowerCase())),
        ]),
      );

      const groups = new Map();
      customers.rows.forEach((row, i) => {
        if (!importable(row)) return;
        const key = row.customer.toLowerCase();
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(i);
      });

      let createdCustomers = 0;
      let createdLocations = 0;
      for (const [key, indexes] of groups) {
        let account = existing.get(key);
        if (!account) {
          try {
            account = (await AccountService.saveAccount({ name: customers.rows[indexes[0]].customer })).data;
            existing.set(key, account);
            existingLocations.set(key, new Set());
            createdCustomers++;
          } catch (error) {
            const message = error?.response?.data?.message || "Couldn't create this customer";
            indexes.forEach((i) => markRow(setCustomers, i, { status: "failed", message }));
            continue;
          }
        }
        const taken = existingLocations.get(key) || new Set();
        existingLocations.set(key, taken);
        for (const i of indexes) {
          const row = customers.rows[i];
          if (taken.has(row.location.toLowerCase())) {
            markRow(setCustomers, i, { status: "skipped", message: "Already on this customer" });
            continue;
          }
          try {
            await CustomerService.saveCustomer({
              companyName: row.location,
              contact: row.contact,
              email: row.email,
              phone: row.phone,
              address: row.address || undefined,
              branch: row.branchId ? { id: row.branchId } : undefined,
              accountId: account.id,
              createdById: currentUser?.id,
            });
            taken.add(row.location.toLowerCase());
            createdLocations++;
            markRow(setCustomers, i, { status: "imported", message: "" });
          } catch (error) {
            markRow(setCustomers, i, {
              status: "failed",
              message: error?.response?.data?.message || "Couldn't save this location",
            });
          }
        }
      }
      if (createdCustomers || createdLocations) {
        onImported({ customers: createdCustomers, locations: createdLocations });
        toast(`Imported ${createdLocations} location(s) across ${groups.size} customer(s)`);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setCustomers((prev) => ({ ...prev, importing: false }));
    }
  };

  // ---- team roster invitations ----

  const parseInvites = (index, dataRows) => {
    const missing = missingColumns(index, ["Email"]);
    if (missing.length) {
      return {
        error: `Missing required column(s): ${missing.join(", ")} — download the template for the expected format.`,
      };
    }
    const seen = new Set();
    const rows = dataRows.map((cells) => {
      const branchName = cellAt(cells, index["branch"]);
      // Same treatment as the customer import: a blank or unrecognised branch
      // invites without one, and the branch is assigned after they join.
      const branch = branchName
        ? branches.find((item) => (item.name || "").trim().toLowerCase() === branchName.toLowerCase())
        : null;
      const row = {
        email: cellAt(cells, index["email"]).toLowerCase(),
        firstName: cellAt(cells, index["first name"]),
        lastName: cellAt(cells, index["last name"]),
        branchName,
        branchId: branch?.id || null,
        status: "ready",
        message: "",
      };
      if (!row.email) return { ...row, status: "error", message: "Email is required" };
      if (!isValidEmail(row.email)) return { ...row, status: "error", message: "Invalid email" };
      if (seen.has(row.email)) return { ...row, status: "error", message: "Duplicate email in this file" };
      seen.add(row.email);
      if (branchName && !branch) {
        return { ...row, message: `Branch "${branchName}" not found — invites without a branch` };
      }
      return row;
    });
    return { rows };
  };

  const importInvites = async () => {
    const role = inviteRoles.find((item) => item.id === Number(inviteRoleId));
    if (!role) {
      toast("Pick the role to invite them with");
      return;
    }
    setInvites((prev) => ({ ...prev, importing: true }));
    let sent = 0;
    for (let i = 0; i < invites.rows.length; i++) {
      const row = invites.rows[i];
      if (!importable(row)) continue;
      try {
        // The branch comes from the file when it names one the company has;
        // otherwise the invite goes out without a branch and it is assigned
        // after they join. Names in the file are roster context only — the
        // invitee types their own name when they accept.
        await InviteService.create({
          invitedBy: currentUser,
          email: row.email,
          role,
          branch: row.branchId ? { id: row.branchId } : null,
          company: currentUser.company,
        });
        sent++;
        markRow(setInvites, i, { status: "imported", message: "" });
      } catch (error) {
        markRow(setInvites, i, {
          status: "failed",
          message: error?.response?.data?.message || "Couldn't send this invitation",
        });
      }
    }
    setInvites((prev) => ({ ...prev, importing: false }));
    if (sent) {
      onImported({ invites: sent });
      toast(`Sent ${sent} invitation(s) as ${role.name}`);
    }
  };

  // ---- vendors ----

  const parseVendors = (index, dataRows) => {
    const missing = missingColumns(index, ["Vendor Name"]);
    if (missing.length) {
      return {
        error: `Missing required column(s): ${missing.join(", ")} — download the template for the expected format.`,
      };
    }
    const seen = new Set();
    const rows = dataRows.map((cells) => {
      const row = { name: cellAt(cells, index["vendor name"]), status: "ready", message: "" };
      if (!row.name) return { ...row, status: "error", message: "Vendor Name is required" };
      const key = row.name.toLowerCase();
      if (seen.has(key)) return { ...row, status: "error", message: "Duplicate vendor in this file" };
      seen.add(key);
      return row;
    });
    return { rows };
  };

  const importVendors = async () => {
    setVendors((prev) => ({ ...prev, importing: true }));
    try {
      // Vendors already on the account are skipped, so re-running an import
      // after fixing failed rows can't double them up.
      const response = await VendorService.getAll("");
      const existing = new Set(
        (Array.isArray(response.data) ? response.data : []).map((vendor) => (vendor.name || "").trim().toLowerCase()),
      );
      let created = 0;
      for (let i = 0; i < vendors.rows.length; i++) {
        const row = vendors.rows[i];
        if (!importable(row)) continue;
        if (existing.has(row.name.toLowerCase())) {
          markRow(setVendors, i, { status: "skipped", message: "Already on your account" });
          continue;
        }
        try {
          await VendorService.saveVendor({ name: row.name });
          existing.add(row.name.toLowerCase());
          created++;
          markRow(setVendors, i, { status: "imported", message: "" });
        } catch (error) {
          markRow(setVendors, i, {
            status: "failed",
            message: error?.response?.data?.message || "Couldn't save this vendor",
          });
        }
      }
      if (created) {
        onImported({ vendors: created });
        toast(`Imported ${created} vendor(s)`);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setVendors((prev) => ({ ...prev, importing: false }));
    }
  };

  // ---- shared presentation ----

  const chip = (status) => {
    const { label, style } = STATUS_CHIPS[status] || STATUS_CHIPS.ready;
    return (
      <span className={`inline-block text-[11px] font-OpenSansSemiBold rounded-full px-[8px] py-[2px] ${style}`}>
        {label}
      </span>
    );
  };

  const previewCell = (value, semibold = false) => (
    <td className="px-2 py-[6px]">
      <p
        className={`text-[12px] ${semibold ? "font-OpenSansSemiBold" : "font-OpenSansRegular"} text-titleColor truncate`}
        title={value}
      >
        {value || "—"}
      </p>
    </td>
  );

  const statusCell = (row) => (
    <td className="px-2 py-[6px]">
      <div className="flex items-center gap-1.5 min-w-0">
        {chip(row.status)}
        {row.message && (
          <span className="text-[11px] font-OpenSansRegular text-subTitleColor truncate" title={row.message}>
            {row.message}
          </span>
        )}
      </div>
    </td>
  );

  const headerCell = (label, width) => (
    <th className={`${width || ""} text-left px-2 py-[6px]`}>
      <span className="text-[11px] font-OpenSansSemiBold text-subTitleColor uppercase tracking-[0.4px]">{label}</span>
    </th>
  );

  const importCard = ({
    title,
    subtitle,
    templateName,
    template,
    state,
    setState,
    parse,
    onImport,
    columns,
    renderRow,
    controls,
    importDisabled,
  }) => {
    const readyCount = state.rows.filter(importable).length;
    const importedCount = state.rows.filter((row) => row.status === "imported").length;
    const skippedCount = state.rows.filter((row) => row.status === "skipped").length;
    const errorCount = state.rows.filter((row) => row.status === "error" || row.status === "failed").length;

    // The whole card is the drop target, so a replacement file can be dropped
    // even while a preview is showing. Imports in flight ignore drops — the
    // rows on screen are the ones being written.
    const setDragging = (dragging) =>
      setState((prev) => (prev.dragging === dragging ? prev : { ...prev, dragging }));

    const dragOver = (e) => {
      e.preventDefault(); // required, or the browser navigates to the file
      if (!state.importing) setDragging(true);
    };

    const dragLeave = (e) => {
      // Moving between the card's own children fires dragleave too; only a
      // leave that lands outside the card ends the highlight.
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDragging(false);
    };

    const drop = (e) => {
      e.preventDefault();
      setDragging(false);
      if (state.importing) return;
      const file = e.dataTransfer?.files?.[0];
      if (file) processFile(file, setState, parse);
    };

    return (
      <div
        onDragOver={dragOver}
        onDragLeave={dragLeave}
        onDrop={drop}
        className={`border-[1px] rounded-[12px] p-4 mb-4 ${
          state.dragging ? "border-blueColor bg-blueColor/5" : "border-borderColor"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[15px] font-OpenSansSemiBold text-titleColor">{title}</p>
            <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor mt-[2px]">{subtitle}</p>
          </div>
          <span
            onClick={() => downloadTemplate(templateName, template)}
            className="text-[12px] font-OpenSansSemiBold text-blueColor cursor-pointer hover:underline shrink-0"
          >
            Download template
          </span>
        </div>

        {controls}

        {state.rows.length === 0 ? (
          <>
            <label
              className={`mt-3 w-full border-dashed border-[2px] rounded-[12px] py-[18px] flex flex-col items-center cursor-pointer hover:border-blueColor ${
                state.dragging ? "border-blueColor bg-blueColor/10" : "border-borderColor"
              }`}
            >
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => readFile(e, setState, parse)}
              />
              <span className="text-[13px] font-OpenSansRegular text-subTitleColor">
                {state.dragging ? "Drop your CSV file here" : "Click to upload your CSV file — or drag & drop it here"}
              </span>
            </label>
            {state.error && (
              <p className="text-[11.5px] font-OpenSansRegular text-red-500 mt-2">
                {state.fileName ? `${state.fileName}: ` : ""}
                {state.error}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="mt-3 border-[1px] border-borderColor rounded-[10px] overflow-hidden">
              <div className="max-h-[220px] overflow-y-auto">
                <table className="w-full table-fixed">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b-[1px] border-borderColor">{columns}</tr>
                  </thead>
                  <tbody>
                    {state.rows.map((row, i) => (
                      <tr key={i} className="border-b-[1px] border-borderColor last:border-b-0">
                        {renderRow(row)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3">
              <span className="text-[12px] font-OpenSansRegular text-subTitleColor truncate" title={state.fileName}>
                {state.fileName} · {readyCount} to import
                {importedCount > 0 && ` · ${importedCount} imported`}
                {skippedCount > 0 && ` · ${skippedCount} skipped`}
                {errorCount > 0 && ` · ${errorCount} with errors`}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <label className="text-[12.5px] font-OpenSansSemiBold text-titleColor border-[1px] border-borderColor rounded-[8px] px-4 h-[32px] flex items-center cursor-pointer hover:border-blueColor">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => readFile(e, setState, parse)}
                  />
                  Choose another file
                </label>
                <button
                  onClick={onImport}
                  disabled={state.importing || readyCount === 0 || importDisabled}
                  className={`text-[12.5px] font-OpenSansSemiBold text-white bg-blueColor rounded-[8px] px-4 h-[32px] flex items-center justify-center gap-2 ${
                    state.importing || readyCount === 0 || importDisabled
                      ? "opacity-40 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  {state.importing ? (
                    <>
                      <Loader variant="button" ariaLabel={`importing-${templateName}`} />
                      Importing...
                    </>
                  ) : (
                    `Import ${readyCount}`
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">Your data</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[16px]">
        Already keep your customers, team roster or vendors in a spreadsheet? Import them here from CSV files — download
        each template to see the columns. Every section is optional: skip anything and add it later in the app.
      </p>

      {importCard({
        title: "Customers & locations",
        subtitle:
          "One row per location — rows sharing a Customer Name become locations of the same customer. Blank or unknown Branch values import without a branch.",
        templateName: "customer_location_import_template.csv",
        template: CUSTOMER_TEMPLATE,
        state: customers,
        setState: setCustomers,
        parse: parseCustomers,
        onImport: importCustomers,
        columns: (
          <>
            {headerCell("Customer", "w-[15%]")}
            {headerCell("Location", "w-[17%]")}
            {headerCell("Address", "w-[16%]")}
            {headerCell("Contact", "w-[11%]")}
            {headerCell("Email", "w-[15%]")}
            {headerCell("Branch", "w-[10%]")}
            {headerCell("Status")}
          </>
        ),
        renderRow: (row) => (
          <>
            {previewCell(row.customer, true)}
            {previewCell(row.location)}
            {previewCell(row.address)}
            {previewCell(row.contact)}
            {previewCell(row.email)}
            {previewCell(row.branchName)}
            {statusCell(row)}
          </>
        ),
      })}

      {importCard({
        title: "Team invitation",
        subtitle:
          "Sends a Makani invitation to every email in the file. Rows naming one of your branches are invited into it; blank or unknown Branch values invite without one, and the branch is assigned after they join.",
        templateName: "user_invite_import_template.csv",
        template: INVITE_TEMPLATE,
        state: invites,
        setState: setInvites,
        parse: parseInvites,
        onImport: importInvites,
        importDisabled: !inviteRoleId,
        controls: (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[13px] font-OpenSansSemiBold text-titleColor">Invite everyone as</span>
            <select
              value={inviteRoleId ?? ""}
              onChange={(e) => setInviteRoleId(Number(e.target.value))}
              className="h-[32px] border-solid border-[1px] border-borderColor rounded-[8px] px-2 text-[13px] font-OpenSansRegular text-titleColor bg-white outline-none cursor-pointer"
            >
              {inviteRoles.length === 0 && <option value="">No roles yet</option>}
              {inviteRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        ),
        columns: (
          <>
            {headerCell("Email", "w-[32%]")}
            {headerCell("First name", "w-[13%]")}
            {headerCell("Last name", "w-[13%]")}
            {headerCell("Branch", "w-[14%]")}
            {headerCell("Status")}
          </>
        ),
        renderRow: (row) => (
          <>
            {previewCell(row.email, true)}
            {previewCell(row.firstName)}
            {previewCell(row.lastName)}
            {previewCell(row.branchName)}
            {statusCell(row)}
          </>
        ),
      })}

      {importCard({
        title: "Vendors",
        subtitle:
          "The suppliers you buy parts and materials from. One vendor per row — vendors already on your account are skipped.",
        templateName: "vendor_import_template.csv",
        template: VENDOR_TEMPLATE,
        state: vendors,
        setState: setVendors,
        parse: parseVendors,
        onImport: importVendors,
        columns: (
          <>
            {headerCell("Vendor", "w-[45%]")}
            {headerCell("Status")}
          </>
        ),
        renderRow: (row) => (
          <>
            {previewCell(row.name, true)}
            {statusCell(row)}
          </>
        ),
      })}
    </>
  );
}

export default YourData;
